import { useMutation } from "@apollo/client";
import { ApolloError } from "@apollo/client";
import {
  Box,
  Checkbox,
  CircularProgress,
  Fab,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  Radio,
  RadioGroup,
  Typography,
  TextField as InputField
} from "@mui/material";
import { loadStripe } from "@stripe/stripe-js";
import { Field, Form, Formik } from "formik";
import { TextField } from "formik-mui";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";

import { CREATE_PAYMENT_FOR_DEPOSIT } from "../../../../../graphql/bookings/createPaymentForDepositMutation";
import { IBooking } from "../../../../../reducers/bookings/types";
import {
  IDepositPaymentInput,
  PaymentMode
} from "../../../../../reducers/invoices/types";
import { IAppState } from "../../../../../store";
import { loadConvergeUS } from "../../../../../utils/converge";
import { FloatInput } from "../../../../common/FloatInput/FloatInput";
import { useSnackBar } from "../../../../common/SnackBarContext/SnackBarContext";
import { SnackBarVariant } from "../../../../common/SnackbarWrapper/SnackbarWrapper";
import {
  PaymentGateway,
  formatGraphQLErrorMessage
} from "../../../../common/utils";
import { createPOSPayment, getPOSPaymentStatus, IPOSPaymentResponse, POSTransactionStatus, POSTransactionTypes } from "../../../POS/api/CommerceSDK";
import { CloseSharp } from "@mui/icons-material";

interface IProps {
  amount: number;
  booking: IBooking;
  handleClose: () => void;
}

const CardTransactionDeposit: React.FC<IProps> = (props) => {
  const navigate = useNavigate();
  const userState = useSelector((state: IAppState) => state.userReducer);
  const { locale, currency, stripeAccountId, cardEnabled, autoChargeEnabled, convergeEnabled, convergeAutoChargeAvailable, convergeUSCredentials } = userState.currentOrganisation;
  const { country } = userState.currentOrganisation.address;
  const snackbar = useSnackBar();
  const [amount, setAmount] = useState<number>(props.amount);
  const [transactionId, setTransactionId] = useState<string>("");
  const [captureLater, setCaptureLater] = useState<boolean>(false);
  const [paymentNote, setPaymentNote] = useState<string>("");
  const [paymentGateway, setPaymentGateway] = useState<string>(
    PaymentGateway.OFFLINE
  );
  const [saveDetails, setSaveDetails] = useState<boolean>(false);
  const [convergePaymentMode, setConvergePaymentMode] = useState<string>("online");
  const [posMode, usingPOSMode] = useState<boolean>(false);
  const [posTransactionInProgress, setPOSTransactionProgress] = useState<boolean>(false);
  const [posTransactionMessage, setPOSTransactionMessage] = useState<string>("");
  const [showPOSErrorSign, setPOSErrorSign] = useState<boolean>(false);
  const [showPOSLoading, setPOSLoading] = useState<boolean>(false);

  const posModeAvailable = Boolean(country === "United States" && convergeUSCredentials?.bms_username && convergeUSCredentials.bms_password);

  useEffect(() => {
    if (
      autoChargeEnabled &&
      (paymentGateway === PaymentGateway.STRIPE ||
        (convergeAutoChargeAvailable &&
          paymentGateway === PaymentGateway.CONVERGE)) &&
      !props.booking.autoChargeLater &&
      !captureLater
    ) {
      setSaveDetails(true);
    }
  }, [autoChargeEnabled, paymentGateway]);

  // Make sure to call `loadStripe` outside of a component’s render to avoid
  // recreating the `Stripe` object on every render.
  const stripePromise: any = loadStripe(
    process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "",
    {
      stripeAccount: stripeAccountId
    }
  );

  const [createPaymentForDeposit, { loading: createDepositPaymentLoading, data: createDepositPaymentData }] = useMutation(
    CREATE_PAYMENT_FOR_DEPOSIT,
    {
      onError: (error: ApolloError) =>
        snackbar({
          message: formatGraphQLErrorMessage(error.message),
          variant: SnackBarVariant.ERROR
        }),
      onCompleted: async (data) => {
        if (paymentGateway === PaymentGateway.OFFLINE) {
          if (data && data.createPaymentForDeposit) {
            props.handleClose()
          }
        } else if (paymentGateway === PaymentGateway.STRIPE) {
          if (
            data?.createPaymentForDeposit?.payment?.id
          ) {
            // Get Stripe.js instance
            const stripe = await stripePromise;
            // When the customer clicks on the button, redirect them to Checkout.
            if (stripe) {
              navigate(`/payment?booking=${props.booking.id}&payment=${data.createPaymentForDeposit.payment.id}`)
            }
          }
        } else if (paymentGateway === PaymentGateway.CONVERGE) {
          const paymentResponse = data.createPaymentForDeposit.payment;
          if (posMode && convergeUSCredentials) {
            setPOSTransactionProgress(true);
            let transactionType: POSTransactionTypes = POSTransactionTypes.SALE;
            if (captureLater) {
              transactionType = POSTransactionTypes.PRE_AUTH;
            }
            const statusResponse: IPOSPaymentResponse = await createPOSPayment(convergeUSCredentials, {
              transactionType,
              amount,
              bookingId: paymentResponse.booking.id || "",
              paymentId: paymentResponse.id,
              saveDetails,
              captureLater: false
            });
            setPOSLoading(true);
            setPOSTransactionMessage(statusResponse.message);
            if (statusResponse.started) {
              const intervalID = setInterval(async () => {
                try {
                  const transactionStatus: POSTransactionStatus = await getPOSPaymentStatus({
                    paymentGatewayId: statusResponse.paymentGatewayId || "",
                    chanId: statusResponse.chanId || "",
                  });

                  switch (transactionStatus) {
                    case POSTransactionStatus.COMPLETE:
                      navigate(`/view-booking?booking=${paymentResponse.booking.id}`);
                      clearInterval(intervalID);
                      break;
                    case POSTransactionStatus.FAILED:
                      setPOSLoading(false);
                      setPOSErrorSign(true);
                      setPOSTransactionMessage("Failed to Process Transaction, please try again later.");
                      clearInterval(intervalID);
                      break;
                    case POSTransactionStatus.IN_PROGRESS:
                      setPOSLoading(true);
                      break;
                    default:
                      break;
                  }
                } catch (error) {
                  console.error(error);
                }
              }, 750);
            } else {
              setPOSErrorSign(true);
            }
          } else {
            if (paymentResponse.convergePaymentSessionUrl) {
              window.location = paymentResponse.convergePaymentSessionUrl;
            } else if (paymentResponse.convergeUSSessionToken) {
              loadConvergeUS({
                paymentSessionToken: paymentResponse.convergeUSSessionToken,
                redirectFunction() {
                  props.handleClose();
                  navigate(`/view-booking?booking=${props.booking.id}`);
                }
              });
            }
          }
        }
      }
    });

  useEffect(() => {
    setAmount(props.amount)
  }, [props.amount])

  const submitStripeForm = () => {
    const payment: IDepositPaymentInput = {
      amount: amount,
      booking: props.booking.id,
      currency,
      paymentMode: PaymentMode.CARD,
      paymentGateway,
      successUrl: `${window.location.protocol}//${window.location.host}/view-booking?booking=${props.booking.id}&status=success`,
      cancelUrl: `${window.location.protocol}//${window.location.host}/view-booking?booking=${props.booking.id}`,
      captureLater,
      saveDetails
    };
    createPaymentForDeposit({
      variables: {
        payment,
        convergePOSTransaction: posMode
      }
    })
  };

  const submitOfflineForm = () => {
    const payment: IDepositPaymentInput = {
      amount: amount,
      booking: props.booking.id,
      currency,
      transactionId: transactionId,
      paymentMode: PaymentMode.CARD,
      paymentGateway: PaymentGateway.OFFLINE,
      paymentNote: paymentNote
    };
    createPaymentForDeposit({
      variables: {
        payment
      }
    })
  };

  const AMOUNT_TOO_HIGH = `Amount must be less than or equal to ${new Intl.NumberFormat(
    locale,
    {
      currency,
      style: "currency"
    }
  ).format(props.amount / 100)}`;

  const stripeFormSchema = Yup.object().shape({
    amount: Yup.number()
      .required()
      .moreThan(0)
      .max(props.amount, AMOUNT_TOO_HIGH)
  });

  const offlineFormSchema = Yup.object().shape({
    amount: Yup.number()
      .required()
      .moreThan(0)
      .max(props.amount, AMOUNT_TOO_HIGH),
    transactionId: Yup.string().required("Reference Number is required.")
  });

  return (
    <Grid container style={{ display: "inline-block", padding: "1rem" }}>
      {!cardEnabled ?
        <Grid container>
          <Typography variant="h4">
            To use this payment mode please enable card payments in Payment and Integration Settings.
          </Typography>
        </Grid> :
        <>
          <Grid item xs={12}>
            <FormControl>
              <RadioGroup
                row
                name="paymentGateway"
                value={paymentGateway}
                onChange={(event) => {
                  setPaymentGateway(event.target.value);
                }}
              >
                <FormControlLabel
                  value={PaymentGateway.OFFLINE}
                  disabled={createDepositPaymentLoading || posTransactionInProgress}
                  control={<Radio />}
                  label={<Typography variant="body1">OTHER</Typography>}
                />
                <FormControlLabel
                  value={PaymentGateway.STRIPE}
                  disabled={createDepositPaymentLoading || posTransactionInProgress}
                  control={<Radio />}
                  label={<Typography variant="body1">STRIPE</Typography>}
                />
                <FormControlLabel
                  value={PaymentGateway.CONVERGE}
                  disabled={createDepositPaymentLoading || posTransactionInProgress}
                  control={<Radio />}
                  label={<Typography variant="body1">CONVERGE</Typography>}
                />
              </RadioGroup>
            </FormControl>
          </Grid>
          {paymentGateway === PaymentGateway.OFFLINE && (
            <>
              <Grid item xs={12}>
                <Formik
                  enableReinitialize
                  validationSchema={offlineFormSchema}
                  initialValues={{
                    amount: amount, transactionId: transactionId
                  }}
                  onSubmit={(values, { setSubmitting }) => {
                    submitOfflineForm();
                    setSubmitting(false);
                  }}
                >
                  {(formikProps) => (
                    <Form>
                      <Grid container spacing={1}>
                        <Grid container item xs={12}>
                          <Box mt={0.2}></Box>
                        </Grid>
                        <Grid item xs={12} md={5} lg={5} xl={5}>
                          <Field
                            component={TextField}
                            placeholder="Reference Number"
                            label="Reference Number"
                            name={"transactionId"}
                            fullWidth
                            required
                            InputProps={{
                              onChange: (
                                e: React.ChangeEvent<HTMLInputElement>
                              ) => {
                                setTransactionId(e.target.value)
                              },
                              value: transactionId
                            }}
                          />
                          <Typography variant={"body2"} color="textSecondary">
                            This could be anything like Receipt Number etc., and is
                            only for internal use.
                          </Typography>
                        </Grid>
                        <Grid container item xs={12}>
                          <Box mt={0.2}></Box>
                        </Grid>
                        <Grid item xs={12} md={5} lg={5} xl={5}>
                          <InputField
                            style={{ width: "100%" }}
                            label="Note"
                            name={"paymentNote"}
                            multiline
                            rows={3}
                            inputProps={{ maxLength: 500 }}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              setPaymentNote(e.target.value)
                            }}
                            variant="outlined"
                            value={paymentNote}
                          />
                        </Grid>
                        <Grid container item xs={12}>
                          <Box mt={0.2}></Box>
                        </Grid>
                        <Grid item xs={12} md={5} lg={5} xl={5}>
                          <Field
                            component={TextField}
                            placeholder="Enter Amount"
                            label="Enter Amount"
                            name={"amount"}
                            disabled
                            fullWidth
                            required
                            InputProps={{
                              onChange: (
                                e: React.ChangeEvent<HTMLInputElement>
                              ) => {
                                setAmount(parseInt(e.target.value))
                              },
                              value: amount,
                              inputComponent: FloatInput as any
                            }}
                            inputProps={{
                              hasCurrencyPrefix: true,
                              allowNegative: false
                            }}
                          />
                        </Grid>
                        <Grid container item xs={12}>
                          <Box mt={0.2}></Box>
                        </Grid>
                        <Grid item xs={12} container justifyContent="flex-start">
                          <Fab
                            variant="extended"
                            size="medium"
                            aria-label="add"
                            type="submit"
                            disabled={createDepositPaymentLoading}
                          >
                            {createDepositPaymentLoading && (
                              <CircularProgress
                                size={14}
                                style={{ color: "white", marginRight: "10px" }}
                              />
                            )}
                            Save
                          </Fab>
                        </Grid>
                      </Grid>
                    </Form>
                  )}
                </Formik>
              </Grid>
            </>
          )}
          {paymentGateway === PaymentGateway.STRIPE &&
            (stripeAccountId ? (
              <>
                <Grid item xs={12}>
                  <FormControl>
                    <RadioGroup
                      row
                      name="captureLater"
                      value={captureLater}
                      onChange={(event) => {
                        if (typeof event.target.value === "string") {
                          if (event.target.value.toLowerCase() === "true") setCaptureLater(true);
                          if (event.target.value.toLowerCase() === "false") setCaptureLater(false);
                        }
                      }}
                    >
                      <FormControlLabel
                        value={false}
                        control={<Radio />}
                        label={
                          <Typography variant="body1">
                            Pay Now
                          </Typography>
                        }
                      />
                      <FormControlLabel
                        value={true}
                        control={<Radio />}
                        label={
                          <Typography variant="body1">
                            Pre Authorise Card
                          </Typography>
                        }
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>
                <Grid item container xs={12}>
                  <Formik
                    enableReinitialize
                    initialValues={{
                      amount: amount,
                      transactionId: transactionId
                    }}
                    validationSchema={stripeFormSchema}
                    onSubmit={(values, { setSubmitting }) => {
                      submitStripeForm();
                      setSubmitting(false);
                    }}
                  >
                    {(formikProps) => (
                      <Form>
                        <Grid item xs={12}>
                          <Field
                            component={TextField}
                            placeholder="Enter Amount"
                            label="Enter Amount"
                            name={"amount"}
                            InputProps={{
                              inputComponent: FloatInput as any
                            }}
                            inputProps={{
                              hasCurrencyPrefix: true,
                              allowNegative: false
                            }}
                            disabled
                            fullWidth
                            required
                          />
                        </Grid>
                        {!props.booking.autoChargeLater && (
                          autoChargeEnabled && !captureLater &&
                          <Grid item xs={12} style={{ marginTop: "1rem" }}>
                            <Typography>
                              Future charges related to the ongoing booking will be charged to this payment method
                            </Typography>
                          </Grid>
                        )}
                        <Grid item xs={12}>
                          <Grid item container>
                            <Box mt={2}></Box>
                          </Grid>
                          <Grid item container>
                            <Fab
                              variant="extended"
                              size="medium"
                              aria-label="add"
                              type="submit"
                              disabled={createDepositPaymentLoading || showPOSLoading}
                            >
                              {createDepositPaymentLoading && (
                                <CircularProgress
                                  size={14}
                                  style={{ color: "white", marginRight: "10px" }}
                                />
                              )}
                              Proceed To Pay
                            </Fab>
                          </Grid>
                        </Grid>
                      </Form>
                    )}
                  </Formik>
                </Grid>
              </>
            ) : (
              <Grid container xs={12}>
                <Typography style={{ margin: "1rem" }}>
                  Please add Stripe Account Id in organisation settings to use this
                  feature.
                </Typography>
              </Grid>
            ))}
          {paymentGateway === PaymentGateway.CONVERGE &&
            (convergeEnabled ? (
              <>
                <Grid item container xs={12}>
                  <Formik
                    enableReinitialize
                    initialValues={{
                      amount: amount,
                      transactionId: transactionId
                    }}
                    validationSchema={stripeFormSchema}
                    onSubmit={(values, { setSubmitting }) => {
                      submitStripeForm();
                      setSubmitting(false);
                    }}
                  >
                    {(formikProps) => (
                      <Form>
                        {posModeAvailable && <Grid item xs={12}>
                          <FormControl>
                            <RadioGroup
                              row
                              name="convergePaymentMode"
                              value={convergePaymentMode}
                              onChange={(event) => {
                                if (event.target.value === "posMode") {
                                  setConvergePaymentMode("posMode");
                                  usingPOSMode(true);
                                } else {
                                  setConvergePaymentMode("online");
                                  usingPOSMode(false);
                                }
                              }}
                            >
                              <FormControlLabel
                                value="online"
                                control={<Radio />}
                                disabled={createDepositPaymentLoading || posTransactionInProgress}
                                label={
                                  <Typography variant="body1">
                                    Online Payment
                                  </Typography>
                                }
                              />
                              <FormControlLabel
                                value="posMode"
                                control={<Radio />}
                                disabled={createDepositPaymentLoading || posTransactionInProgress}
                                label={
                                  <Typography variant="body1">POS Payment</Typography>
                                }
                              />
                            </RadioGroup>
                          </FormControl>
                        </Grid>
                        }
                        <Grid item xs={12}>
                          <FormControl>
                            <RadioGroup
                              row
                              name="captureLater"
                              value={captureLater}
                              onChange={(event) => {
                                if (typeof event.target.value === "string") {
                                  if (event.target.value.toLowerCase() === "true") setCaptureLater(true);
                                  if (event.target.value.toLowerCase() === "false") setCaptureLater(false);
                                }
                              }}
                            >
                              <FormControlLabel
                                value={false}
                                control={<Radio />}
                                disabled={createDepositPaymentLoading || posTransactionInProgress}
                                label={
                                  <Typography variant="body1">
                                    Pay Now
                                  </Typography>
                                }
                              />
                              <FormControlLabel
                                value={true}
                                control={<Radio />}
                                disabled={createDepositPaymentLoading || posTransactionInProgress}
                                label={
                                  <Typography variant="body1">
                                    Pre Authorise Card
                                  </Typography>
                                }
                              />
                            </RadioGroup>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                          <Field
                            component={TextField}
                            placeholder="Enter Amount"
                            label="Enter Amount"
                            name={"amount"}
                            InputProps={{
                              inputComponent: FloatInput as any
                            }}
                            inputProps={{
                              hasCurrencyPrefix: true,
                              allowNegative: false
                            }}
                            disabled
                            fullWidth
                            required
                          />
                        </Grid>
                        {!props.booking.autoChargeLater && (
                          autoChargeEnabled && !captureLater && convergeAutoChargeAvailable &&
                          <Grid item xs={12} style={{ marginTop: "1rem" }}>
                            <Typography>
                              Future charges related to the ongoing booking will be charged to this payment method
                            </Typography>
                          </Grid>
                        )}
                        <Grid item xs={12}>
                          <Grid item container>
                            <Box mt={2}></Box>
                          </Grid>
                          <Grid item container>
                            <Fab
                              variant="extended"
                              size="medium"
                              aria-label="add"
                              type="submit"
                              disabled={createDepositPaymentLoading || posTransactionInProgress || showPOSLoading}
                            >
                              {createDepositPaymentLoading && (
                                <CircularProgress
                                  size={14}
                                  style={{ color: "white", marginRight: "10px" }}
                                />
                              )}
                              Proceed To Pay
                            </Fab>
                          </Grid>
                        </Grid>
                        {posTransactionMessage.length > 0 &&
                          <Grid item container xs={12} spacing={1} style={{ margin: "1rem" }}>
                            {showPOSLoading && <Grid item xs={1}>
                              <CircularProgress />
                            </Grid>}
                            {showPOSErrorSign && <Grid item xs={1}>
                              <CloseSharp />
                            </Grid>}
                            <Grid item xs={11}>
                              <Typography >
                                {posTransactionMessage}
                              </Typography>
                            </Grid>
                          </Grid>
                        }
                      </Form>
                    )}
                  </Formik>
                </Grid>
              </>
            ) : (
              <Grid container xs={12}>
                <Typography style={{ margin: "1rem" }}>
                  Please add Converge credentials in Payments & Integrations settings to use this
                  feature.
                </Typography>
              </Grid>
            ))}
        </>}
    </Grid>
  );
};

export default CardTransactionDeposit;